import { Controller } from "@hotwired/stimulus"

// "Hide from my list" (Phelipe 2026-10-26). Each person hides projects from
// THEIR OWN Construction / Design / Bidding / All Phases lists; nobody else
// is affected. One controller, two uses:
//
// 1. On a card (shared/_project_hide_menu): the ⋯ button opens a small menu
//    with "Hide from my list"; "Unhide" shows instead when the card is a
//    hidden project being shown. The menu sits inside the card's link, so
//    every click is stopped before it can open the project.
//      <span data-controller="hide-project"
//            data-hide-project-project-id-value="123"
//            data-hide-project-hidden-value="false"> … </span>
//
// 2. The "Hidden (N)" eye button (shared/_hidden_projects_toggle): flips the
//    person's show-hidden setting and reloads only the list's turbo-frames.
//    It also carries the URLs, words and state the card controllers read
//    (data-hide-url, data-labels, data-show-hidden, data-count…).
//
// The menu panel is put on <body> with position: fixed, because the cards
// clip their content (overflow-hidden) and move on hover (transform).

const DEFAULT_LABELS = {
  hide: "Hide from my list",
  hidden_toast: "Hidden from your list.",
  unhidden_toast: "Back in your list.",
  undo: "Undo",
  hide_failed: "Couldn't hide this project. Please try again.",
  unhide_failed: "Couldn't unhide this project. Please try again.",
  toggle_failed: "Couldn't change this. Please try again."
}

const TOGGLE_ON = ["bg-blue-50", "border-blue-300", "text-blue-700"]
const TOGGLE_OFF = ["bg-white", "border-gray-300", "text-text-muted", "hover:text-gray-700"]

export default class extends Controller {
  static targets = ["menuButton", "unhideButton"]
  static values = {
    projectId: Number,
    hidden: { type: Boolean, default: false }
  }

  disconnect() {
    this._closeMenu()
  }

  // Keep the card's link (and the All Phases board's drag) out of it.
  stop(event) {
    event.stopPropagation()
  }

  // ── Card menu ───────────────────────────────────────────────────

  toggleMenu(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this._menu) {
      this._closeMenu()
    } else {
      this._openMenu()
    }
  }

  _openMenu() {
    // Only one menu open at a time, across all cards.
    document.dispatchEvent(new CustomEvent("hide-project:close-menus"))

    const labels = this._labels()
    const panel = document.createElement("div")
    panel.setAttribute("role", "menu")
    panel.className = "fixed z-50 min-w-[200px] bg-white border border-gray-300 rounded-lg shadow-lg py-1"

    const item = document.createElement("button")
    item.type = "button"
    item.setAttribute("role", "menuitem")
    item.className = "w-full text-left px-3 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50 cursor-pointer flex items-center gap-2 whitespace-nowrap"
    if (labels.hide_icon) {
      const iconWrap = document.createElement("span")
      iconWrap.className = "inline-flex shrink-0 text-gray-400"
      iconWrap.innerHTML = labels.hide_icon
      item.appendChild(iconWrap)
    }
    const text = document.createElement("span")
    text.textContent = labels.hide
    item.appendChild(text)
    item.addEventListener("click", (e) => {
      e.preventDefault()
      e.stopPropagation()
      this._closeMenu()
      this.hide()
    })
    panel.appendChild(item)
    panel.addEventListener("mousedown", (e) => e.stopPropagation())
    panel.addEventListener("click", (e) => e.stopPropagation())

    document.body.appendChild(panel)
    this._menu = panel
    this._positionMenu()
    item.focus({ preventScroll: true })

    this._onOutside = (e) => {
      if (this._menu && !this._menu.contains(e.target) && !this.element.contains(e.target)) this._closeMenu()
    }
    this._onKey = (e) => { if (e.key === "Escape") this._closeMenu() }
    this._onMove = () => this._closeMenu()
    this._onCloseAll = () => this._closeMenu()
    document.addEventListener("click", this._onOutside, true)
    document.addEventListener("keydown", this._onKey)
    window.addEventListener("scroll", this._onMove, true)
    window.addEventListener("resize", this._onMove)
    document.addEventListener("hide-project:close-menus", this._onCloseAll)
  }

  _positionMenu() {
    if (!this._menu || !this.hasMenuButtonTarget) return
    const rect = this.menuButtonTarget.getBoundingClientRect()
    const width = this._menu.offsetWidth
    const height = this._menu.offsetHeight
    let left = rect.right - width
    if (left < 8) left = 8
    let top = rect.bottom + 4
    if (top + height > window.innerHeight - 8) top = Math.max(8, rect.top - height - 4)
    this._menu.style.left = `${left}px`
    this._menu.style.top = `${top}px`
  }

  _closeMenu() {
    if (!this._menu) return
    this._menu.remove()
    this._menu = null
    document.removeEventListener("click", this._onOutside, true)
    document.removeEventListener("keydown", this._onKey)
    window.removeEventListener("scroll", this._onMove, true)
    window.removeEventListener("resize", this._onMove)
    document.removeEventListener("hide-project:close-menus", this._onCloseAll)
  }

  // ── Hide / Unhide ───────────────────────────────────────────────

  async hide() {
    const labels = this._labels()
    const toggle = this._toggle()
    const projectId = this.projectIdValue
    const card = this._card()
    const frame = card?.closest("turbo-frame")

    let data
    try {
      data = await this._request("POST", toggle?.dataset.hideUrl || "/users/hide_project", projectId)
    } catch {
      this._toast(labels.hide_failed, "alert")
      return
    }

    // With the eye on, a hidden project stays in the list, grayed; otherwise it leaves.
    let placeholder = null
    if (toggle?.dataset.showHidden === "true") {
      this._setHiddenLook(true)
    } else if (card) {
      placeholder = { parent: card.parentNode, next: card.nextSibling, card }
      card.remove()
      this._bumpFrameCount(frame, -1)
    }
    this._setCount(data.hidden_count)

    this._toast(labels.hidden_toast, "notice", {
      label: labels.undo,
      run: () => this._undoHide(projectId, placeholder, frame)
    })
  }

  async _undoHide(projectId, placeholder, frame) {
    const labels = this._labels()
    const toggle = this._toggle()
    let data
    try {
      data = await this._request("DELETE", toggle?.dataset.unhideUrl || "/users/unhide_project", projectId)
    } catch {
      this._toast(labels.unhide_failed, "alert")
      return
    }
    if (placeholder) {
      const { parent, next, card } = placeholder
      if (parent && parent.isConnected) {
        parent.insertBefore(card, next && next.parentNode === parent ? next : null)
        this._bumpFrameCount(frame, +1)
      } else if (frame?.src) {
        frame.reload()
      }
    } else {
      this._setHiddenLook(false)
    }
    this._setCount(data.hidden_count)
  }

  async unhide(event) {
    event.preventDefault()
    event.stopPropagation()
    const labels = this._labels()
    const toggle = this._toggle()
    let data
    try {
      data = await this._request("DELETE", toggle?.dataset.unhideUrl || "/users/unhide_project", this.projectIdValue)
    } catch {
      this._toast(labels.unhide_failed, "alert")
      return
    }
    this._setHiddenLook(false)
    this._setCount(data.hidden_count)
    this._toast(labels.unhidden_toast, "notice")
  }

  _setHiddenLook(hidden) {
    this.hiddenValue = hidden
    this._card()?.classList.toggle("opacity-60", hidden)
    if (this.hasMenuButtonTarget) this.menuButtonTarget.hidden = hidden
    if (this.hasUnhideButtonTarget) this.unhideButtonTarget.hidden = !hidden
  }

  // ── "Hidden (N)" eye button ─────────────────────────────────────

  async toggleShowHidden(event) {
    event.preventDefault()
    const button = this.element
    if (button.dataset.busy === "true") return
    button.dataset.busy = "true"
    try {
      const resp = await fetch(button.dataset.toggleUrl, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrf(), "Accept": "application/json" }
      })
      if (!resp.ok) throw new Error("Failed")
      const data = await resp.json()
      this._applyToggleState(button, data.show_hidden)
      const selector = button.dataset.frames
      if (selector) {
        document.querySelectorAll(selector).forEach((frame) => {
          if (frame.src && typeof frame.reload === "function") frame.reload()
        })
      }
    } catch {
      this._toast(this._labels().toggle_failed, "alert")
    } finally {
      button.dataset.busy = "false"
    }
  }

  _applyToggleState(button, shown) {
    button.dataset.showHidden = shown ? "true" : "false"
    button.setAttribute("aria-pressed", shown ? "true" : "false")
    TOGGLE_ON.forEach((c) => button.classList.toggle(c, shown))
    TOGGLE_OFF.forEach((c) => button.classList.toggle(c, !shown))
    button.querySelectorAll("[data-eye]").forEach((el) => {
      el.hidden = (el.dataset.eye === "on") !== shown
    })
    const badge = button.querySelector("[data-hide-project-count]")
    if (badge) {
      badge.classList.toggle("bg-blue-100", shown)
      badge.classList.toggle("bg-gray-200", !shown)
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────

  _toggle() {
    return document.querySelector("[data-hide-project-toggle]")
  }

  _card() {
    return this.element.closest("[data-hide-project-card]")
  }

  _labels() {
    const raw = this._toggle()?.dataset.labels
    if (!raw) return DEFAULT_LABELS
    try {
      return { ...DEFAULT_LABELS, ...JSON.parse(raw) }
    } catch {
      return DEFAULT_LABELS
    }
  }

  _setCount(count) {
    const toggle = this._toggle()
    if (!toggle || typeof count !== "number") return
    toggle.dataset.count = String(count)
    const badge = toggle.querySelector("[data-hide-project-count]")
    if (badge) badge.textContent = count
    toggle.hidden = count < 1
  }

  // "N projects" above the list, or the All Phases column counter.
  _bumpFrameCount(frame, delta) {
    if (!frame) return
    const el = frame.querySelector("[data-project-total]") || document.getElementById(`${frame.id}-count`)
    if (!el) return
    const n = parseInt(el.textContent, 10)
    if (!Number.isNaN(n)) el.textContent = Math.max(0, n + delta)
  }

  async _request(method, url, projectId) {
    const body = new FormData()
    body.append("project_id", projectId)
    const resp = await fetch(url, {
      method,
      body,
      headers: { "X-CSRF-Token": this._csrf(), "Accept": "application/json" }
    })
    if (!resp.ok) throw new Error("Failed")
    return resp.json()
  }

  _csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  // Same toast as the rest of the app (flash-toast-notice / -alert + the
  // flash controller, 5s), with an optional Undo button.
  _toast(message, type = "notice", action = null) {
    const cssType = type === "alert" ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    if (action) {
      const undo = document.createElement("button")
      undo.type = "button"
      undo.className = "shrink-0 ml-1 text-sm font-semibold underline underline-offset-2 cursor-pointer"
      undo.textContent = action.label
      undo.addEventListener("click", () => action.run(), { once: true })
      el.appendChild(undo)
    }
    document.body.prepend(el)
  }
}
